import { useLocation, Link } from 'react-router-dom'
import { Users, ShieldCheck, Activity, Settings, type LucideIcon } from 'lucide-react'
import { clsx } from 'clsx'
import { useAuth } from '../../contexts/useAuth'
import type { JwtPayload } from '../../lib/auth'

interface NavItem {
  label: string
  icon: LucideIcon
  path: string
  roles?: JwtPayload['role'][]
}

const navItems: NavItem[] = [
  // The v1 officer destinations (Dashboard / Funds / Queue / Requests /
  // Upload) were RETIRED 2026-07-29 — their routes now redirect to /v2. They
  // are gone from this nav rather than left pointing at a redirect: a menu
  // item that bounces you elsewhere is the same "surface that lies" problem
  // the Policy screen had. What remains here is the admin shell only; the
  // officer's app is /v2.
  {
    label: 'User Management',
    icon: Users,
    path: '/admin/users',
    roles: ['admin'],
  },
  {
    label: 'EQS Admin',
    icon: ShieldCheck,
    path: '/admin/eqs',
    roles: ['admin'],
  },
  {
    label: 'Policy Config',
    icon: Settings,
    path: '/admin/policy',
    roles: ['admin', 'manager', 'ops_officer'],
  },
  {
    label: 'System Health',
    icon: Activity,
    path: '/admin/health',
    roles: ['admin'],
  },
]

function isActive(currentPath: string, itemPath: string): boolean {
  if (itemPath === '/dashboard') return currentPath === '/dashboard'
  return currentPath.startsWith(itemPath)
}

export default function Sidebar() {
  const location = useLocation()
  const { user } = useAuth()

  const visibleItems = navItems.filter(
    (item) => !item.roles || (user && item.roles.includes(user.role))
  )

  return (
    <aside className="fixed left-0 top-0 bottom-0 w-56 flex flex-col bg-sidebar-bg z-40">
      {/* Logo */}
      <div className="flex items-center h-14 px-5">
        <span className="text-white text-lg font-bold tracking-wide">NOLOS</span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 mt-2 flex flex-col gap-0.5 px-2">
        {visibleItems.map((item) => {
          const active = isActive(location.pathname, item.path)
          const Icon = item.icon
          return (
            <Link
              key={item.path}
              to={item.path}
              className={clsx(
                'flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors',
                active
                  ? 'bg-sidebar-active text-white border-l-[3px] border-sidebar-active-border'
                  : 'text-slate-300 hover:bg-sidebar-hover hover:text-white border-l-[3px] border-transparent'
              )}
            >
              <Icon size={18} strokeWidth={1.75} />
              <span>{item.label}</span>
            </Link>
          )
        })}
      </nav>

      {/* User section at bottom */}
      {user && (
        <div className="p-4 border-t border-white/10">
          <div className="text-white text-sm font-medium truncate">{user.name}</div>
          <div className="text-slate-400 text-xs mt-0.5 capitalize">
            {user.role.replace('_', ' ')}
          </div>
        </div>
      )}
    </aside>
  )
}
